/**
 * Chunking for the bulk override commit.
 *
 * Every GraphQL service on the platform sits behind apollo-server-lambda's default express
 * body-parser, whose json limit is 100kb — for admin-portal traffic the gatekeeper rejects
 * anything larger with an opaque 413 before core ever sees it. One PRODUCT_UPDATE item
 * serialises to ~120-140 bytes, so a single-mutation commit hits that wall at roughly 860
 * items (NEEDS_REVIEW at ~1150, NDC_UPDATE at ~1350).
 *
 * The server is already batch-native above its async threshold — every mutation call gets its
 * own batchId, its own SQS chunks and its own websocket events, and nothing server-side assumes
 * one batch per user — so the client can split a large run into several mutations and aggregate
 * the outcome. This module is the pure half of that: how a run is split, how the sequential
 * submit loop behaves, and how a failed submit is classified. All state stays in the hook.
 */

/* The budget is BYTES, not items: the cliff is a byte limit, and item sizes differ per
 * operation and grow whenever the input type gains a field. An item cap alone would silently
 * regress the day that happens; the byte budget is self-correcting. ~60KB against the 100kb
 * wall, because the whole envelope (query text, operationType, productId, JSON punctuation)
 * rides on top of the items. */
export const CHUNK_BYTE_BUDGET = 60 * 1024;

const DEFAULT_MAX_ITEMS = 400;

/* Above 800 even the smallest items (ndcOverrides, ~75 bytes) approach the wall, and a chunk
 * that size would also exceed the ~350-item ceiling the synchronous path survives if the async
 * kill switch is ever off — the byte budget usually cuts in first, but the cap must hold on its
 * own. Below 1 the loop cannot make progress. */
const MIN_CHUNK_ITEMS = 1;
const MAX_CHUNK_ITEMS = 800;

/* NOT the `Number(env) || 0` idiom the row limit uses: there 0 means "no cap", here a chunk
 * size of 0 is an infinite loop. Anything unset, unparsable or absurd falls back to the
 * default; anything real is clamped into the range the platform can carry. */
export const clampChunkSize = (value) => {
  const size = Number(value);

  if (!Number.isFinite(size) || size < MIN_CHUNK_ITEMS) return DEFAULT_MAX_ITEMS;

  return Math.min(Math.floor(size), MAX_CHUNK_ITEMS);
};

export const MAX_ITEMS_PER_CHUNK = clampChunkSize(process.env.REACT_APP_BULK_OVERRIDE_CHUNK_SIZE);

/**
 * Split the commit's items into chunks that each fit one request.
 *
 * Greedy accumulation in submission order: a chunk closes when the next item would push it past
 * the byte budget or the item cap. A single item larger than the whole budget still forms its
 * own chunk — refusing it here would silently drop a staged row, and one item can never
 * actually reach 60KB.
 */
export const buildCommitChunks = (
  items,
  { maxBytes = CHUNK_BYTE_BUDGET, maxItems = MAX_ITEMS_PER_CHUNK } = {}
) => {
  const chunks = [];
  let current = [];
  let currentBytes = 0;

  items.forEach((item) => {
    /* .length counts UTF-16 units, not bytes — identical for today's all-ASCII fields, and the
     * 40KB of headroom under the 100kb wall absorbs the drift if a free-text field ever joins
     * the payload. +1 for the separating comma. */
    const itemBytes = JSON.stringify(item).length + 1;

    const isFull =
      current.length > 0 && (currentBytes + itemBytes > maxBytes || current.length >= maxItems);

    if (isFull) {
      chunks.push(current);
      current = [];
      currentBytes = 0;
    }

    current.push(item);
    currentBytes += itemBytes;
  });

  if (current.length > 0) chunks.push(current);

  return chunks;
};

/**
 * Definite vs indeterminate submit failure — the distinction the recovery story hangs on.
 *
 * A definite rejection (GraphQL errors on the response, or a non-retriable 4xx) means the
 * server refused the chunk, so its rows are safe to keep staged and retry. Anything else — a
 * 5xx, a gateway timeout, a dropped connection — means the chunk MAY be running server-side:
 * core queues the work before the response travels back, so "the request failed" does not mean
 * "the work was not accepted", and retrying would re-apply overrides that already landed.
 */
export const isDefiniteRejection = (error) => {
  const status = error?.response?.status;
  const hasGraphqlErrors =
    Array.isArray(error?.response?.errors) && error.response.errors.length > 0;

  // GraphQL-level errors arrive with HTTP 200; only a 5xx transport underneath makes them
  // ambiguous.
  if (hasGraphqlErrors) return !status || status < 500;

  // 408 and 429 are the two 4xx codes where the server may still do the work.
  return Boolean(status) && status >= 400 && status < 500 && status !== 408 && status !== 429;
};

/**
 * Submit chunks one at a time and report how far the run got.
 *
 * Sequential on purpose: the backend fans each batch onto SQS itself, so parallel submits would not
 * finish sooner — but a mid-flight failure with requests in the air leaves no way to say which
 * chunks landed. One at a time, the failure boundary is always a determinate prefix.
 *
 * Never throws. `error` is null when every chunk was submitted; otherwise it is the failure and
 * `submittedIds` names exactly the rows the server accepted before it. A chunk can come back
 * QUEUED (batchId, above the server's async threshold) or synchronous (results — the small
 * remainder chunk, or every chunk when the async kill switch is off); `batches` and `sync`
 * carry the two shapes side by side for the caller to aggregate.
 *
 * `onChunkAccepted` fires the moment a chunk is QUEUED, before the next submit. The progress
 * tracker registers each batch here rather than at the end: core publishes a batch's first
 * events before that batch's own mutation even resolves, so a tracker that waits for the full
 * list has already missed them.
 */
export const submitChunksSequentially = async ({ chunks, submit, onChunkAccepted }) => {
  const batches = [];
  const sync = { succeeded: 0, failed: 0, total: 0 };
  const submittedIds = [];

  for (let index = 0; index < chunks.length; index += 1) {
    const chunk = chunks[index];

    try {
      // eslint-disable-next-line no-await-in-loop
      const response = await submit(chunk);

      if (response?.status === 'QUEUED') {
        /* Fail loudly rather than track blindly: a QUEUED response without a batchId can never
         * match a websocket event, so the run could only end on its 15-minute timeout. Thrown
         * WITHOUT a `response`, it classifies as indeterminate — which is the truth: the server
         * accepted the work, we just cannot follow it. */
        if (!response.batchId) {
          throw new Error('bulk override: QUEUED response carried no batchId');
        }

        const batch = { batchId: response.batchId, accepted: response.accepted ?? chunk.length };

        batches.push(batch);
        onChunkAccepted?.(batch);
      } else {
        const results = response?.results ?? [];
        const failedCount = results.filter(({ success }) => !success).length;

        sync.total += results.length;
        sync.failed += failedCount;
        sync.succeeded += results.length - failedCount;
      }

      submittedIds.push(...chunk.map(({ lineItemId }) => lineItemId));
    } catch (requestError) {
      return { batches, sync, submittedIds, error: requestError };
    }
  }

  return { batches, sync, submittedIds, error: null };
};
